import { useEffect, useState } from "react";
import { Loader2, Save } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { AdminTitle, Field } from "@/pages/admin/AdminUI";
import ImageUpload from "@/components/ImageUpload";
import { useSettings } from "@/context/SettingsContext";
import { toast } from "sonner";

const SECTIONS = ["hero", "services", "why", "how", "testimonials", "faq", "blog", "cta", "contact"];

export default function AdminSettings() {
  const { reload } = useSettings();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("general");

  useEffect(() => { api.get("/settings").then((r) => setS({ sections: {}, how_it_works: [], ...r.data })).catch(() => setS({})); }, []);

  const set = (k) => (e) => setS({ ...s, [k]: e.target.value });
  const setSection = (name, val) => setS({ ...s, sections: { ...s.sections, [name]: val } });
  const setHow = (i, k, v) => { const h = [...(s.how_it_works || [])]; h[i] = { ...h[i], [k]: v }; setS({ ...s, how_it_works: h }); };

  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/settings", s); toast.success("Settings saved"); reload(); }
    catch (e) { toast.error(apiError(e)); }
    setSaving(false);
  };

  if (s === null) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-amber-400" size={36} /></div>;

  const tabs = [["general", "General"], ["hero", "Homepage & Hero"], ["contact", "Contact & Social"], ["seo", "SEO & Analytics"], ["sections", "Sections & Steps"]];

  return (
    <div>
      <AdminTitle title="Website Settings" action={<button onClick={save} disabled={saving} className="gold-btn text-sm" data-testid="settings-save-btn">{saving ? <Loader2 className="animate-spin" size={16} /> : <><Save size={16} /> Save</>}</button>} />
      <div className="flex flex-wrap gap-2 mb-6">
        {tabs.map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`settings-tab-${id}`}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${tab === id ? "bg-gradient-to-r from-amber-400 to-amber-600 text-black" : "border border-amber-500/20 text-slate-300"}`}>{label}</button>
        ))}
      </div>

      <div className="card-lux p-6 space-y-4 max-w-3xl">
        {tab === "general" && (<>
          <Field label="Website Name"><input className="input-lux" value={s.site_name || ""} onChange={set("site_name")} data-testid="settings-sitename" /></Field>
          <ImageUpload label="Logo" value={s.logo || ""} onChange={(v) => setS({ ...s, logo: v })} testid="settings-logo" />
          <ImageUpload label="Favicon" value={s.favicon || ""} onChange={(v) => setS({ ...s, favicon: v })} testid="settings-favicon" />
          <Field label="Tagline"><input className="input-lux" value={s.tagline || ""} onChange={set("tagline")} /></Field>
          <Field label="Footer Text"><textarea rows={3} className="input-lux resize-none" value={s.footer_text || ""} onChange={set("footer_text")} /></Field>
          <Field label="Copyright"><input className="input-lux" value={s.copyright || ""} onChange={set("copyright")} /></Field>
          <Field label="Business Address"><input className="input-lux" value={s.business_address || ""} onChange={set("business_address")} /></Field>
          <Field label="About Text"><textarea rows={4} className="input-lux resize-none" value={s.about_text || ""} onChange={set("about_text")} /></Field>
        </>)}
        {tab === "hero" && (<>
          <Field label="Hero Title"><input className="input-lux" value={s.hero_title || ""} onChange={set("hero_title")} data-testid="settings-hero-title" /></Field>
          <Field label="Hero Subtitle"><textarea rows={3} className="input-lux resize-none" value={s.hero_subtitle || ""} onChange={set("hero_subtitle")} /></Field>
          <Field label="CTA Text"><input className="input-lux" value={s.cta_text || ""} onChange={set("cta_text")} /></Field>
        </>)}
        {tab === "contact" && (<>
          <Field label="Contact Email"><input className="input-lux" value={s.contact_email || ""} onChange={set("contact_email")} data-testid="settings-contact-email" /></Field>
          <Field label="Telegram URL"><input className="input-lux" value={s.telegram || ""} onChange={set("telegram")} /></Field>
          <Field label="WhatsApp URL"><input className="input-lux" value={s.whatsapp || ""} onChange={set("whatsapp")} /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Instagram"><input className="input-lux" value={s.instagram || ""} onChange={set("instagram")} /></Field>
            <Field label="Facebook"><input className="input-lux" value={s.facebook || ""} onChange={set("facebook")} /></Field>
            <Field label="LinkedIn"><input className="input-lux" value={s.linkedin || ""} onChange={set("linkedin")} /></Field>
            <Field label="Twitter/X"><input className="input-lux" value={s.twitter || ""} onChange={set("twitter")} /></Field>
          </div>
        </>)}
        {tab === "seo" && (<>
          <Field label="SEO Title"><input className="input-lux" value={s.seo_title || ""} onChange={set("seo_title")} /></Field>
          <Field label="SEO Description"><textarea rows={2} className="input-lux resize-none" value={s.seo_description || ""} onChange={set("seo_description")} /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Google Analytics ID"><input className="input-lux" value={s.google_analytics_id || ""} onChange={set("google_analytics_id")} data-testid="settings-ga" /></Field>
            <Field label="Google Tag Manager ID"><input className="input-lux" value={s.google_tag_manager_id || ""} onChange={set("google_tag_manager_id")} /></Field>
            <Field label="Meta Pixel ID"><input className="input-lux" value={s.meta_pixel_id || ""} onChange={set("meta_pixel_id")} /></Field>
            <Field label="TikTok Pixel ID"><input className="input-lux" value={s.tiktok_pixel_id || ""} onChange={set("tiktok_pixel_id")} /></Field>
          </div>
          <p className="text-xs text-slate-500">Tracking scripts fire only when IDs are set. Purchase events fire only after confirmed payment.</p>
        </>)}
        {tab === "sections" && (<>
          <div>
            <label className="label-lux">Homepage Sections (enable/disable)</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-3">
              {SECTIONS.map((name) => (
                <label key={name} className="flex items-center gap-2 text-sm text-slate-300 capitalize card-lux px-3 py-2">
                  <input type="checkbox" checked={s.sections?.[name] !== false} onChange={(e) => setSection(name, e.target.checked)} className="accent-amber-500" data-testid={`section-toggle-${name}`} /> {name}
                </label>
              ))}
            </div>
          </div>
          <div className="pt-4">
            <label className="label-lux">How It Works Steps</label>
            <div className="space-y-3 mt-3">
              {(s.how_it_works || []).map((h, i) => (
                <div key={i} className="grid grid-cols-12 gap-2">
                  <input className="input-lux col-span-1 !py-2 text-center" value={h.step} onChange={(e) => setHow(i, "step", e.target.value)} />
                  <input className="input-lux col-span-4 !py-2 text-xs" value={h.title} onChange={(e) => setHow(i, "title", e.target.value)} />
                  <input className="input-lux col-span-7 !py-2 text-xs" value={h.desc} onChange={(e) => setHow(i, "desc", e.target.value)} />
                </div>
              ))}
            </div>
          </div>
        </>)}
      </div>
    </div>
  );
}
